import React, { useEffect, useState } from "react";
import {
  CheckSquare, Square, Plus, Loader2, Trash2, Sparkles, Calendar,
  Flag, ChevronDown, ChevronUp, X, Bell, Star,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/hooks/useSeo";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const INTENTS = [
  { k: "money",   e: "💰", best: "Thu · Fri" },
  { k: "career",  e: "💼", best: "Sun · Wed" },
  { k: "love",    e: "❤️", best: "Fri · Mon" },
  { k: "health",  e: "💚", best: "Wed · Tue" },
  { k: "travel",  e: "✈️", best: "Mon · Wed" },
  { k: "study",   e: "📚", best: "Wed · Sun" },
  { k: "wedding", e: "💍", best: "Thu · Sun" },
];
const PRIORITY_COLOR = { high: "text-rose-600", med: "text-amber-600", low: "text-stone-400" };
const PRIORITY_BG    = { high: "bg-rose-50 border-rose-200", med: "bg-amber-50 border-amber-200", low: "bg-stone-50 border-stone-200" };

/**
 * /tasks — Personal task manager with astro-intent integration.
 *   Every task can carry an astro_intent (money/career/love/…) which surfaces
 *   the next auspicious weekday inline — helpful, not preachy.
 */
export default function Tasks() {
  const { user } = useAuth();
  const [tasks, setTasks]   = useState([]);
  const [lists, setLists]   = useState([]);
  const [summary, setSummary] = useState(null);
  const [widget,  setWidget]  = useState(null);   // Astro best-days widget
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [filter, setFilter]   = useState({ status: "open", list_id: null });
  useSeo({ title: "Tasks · astro-guided to-do · find2near",
           description: "Personal to-do list with auspicious-day hints from Vedic astrology." });

  const load = async () => {
    setLoading(true);
    try {
      const [tr, lr, sr, wr] = await Promise.all([
        api.get("/tasks", { params: { status: filter.status || undefined, list_id: filter.list_id || undefined } }),
        api.get("/tasks/lists"),
        api.get("/tasks/summary"),
        api.get("/tasks/astro-widget"),
      ]);
      setTasks(tr.data); setLists(lr.data); setSummary(sr.data); setWidget(wr.data);
    } catch (e) { toast.error("Couldn't load tasks"); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [user, filter]);

  // ── Foreground reminders — poll upcoming due tasks and fire Notification ──
  useEffect(() => {
    if (!user) return;
    const fired = new Set();
    const check = async () => {
      try {
        const r = await api.get("/tasks/reminders/upcoming");
        (r.data.tasks || []).forEach(t => {
          if (fired.has(t.task_id)) return;
          fired.add(t.task_id);
          if (typeof Notification !== "undefined" && Notification.permission === "granted") {
            const n = new Notification(`⏰ Due soon: ${t.title}`, {
              body:  t.notes || `Due ${t.due_at?.slice(11, 16) || ""}`,
              tag:   `task-${t.task_id}`,
            });
            n.onclick = () => { window.focus(); n.close(); };
          }
        });
      } catch { /* silent */ }
    };
    check();
    const id = setInterval(check, 60_000);
    return () => clearInterval(id);
  }, [user]);

  const askNotifs = async () => {
    if (typeof Notification === "undefined") { toast.error("Notifications not supported"); return; }
    const perm = await Notification.requestPermission();
    if (perm === "granted") toast.success("Reminders on — you'll be pinged when tasks are due");
    else toast.error("Enable notifications in browser settings");
  };

  const toggle = async (t) => {
    try {
      const r = await api.post(`/tasks/${t.task_id}/toggle`);
      setTasks(ts => ts.map(x => x.task_id === t.task_id ? { ...x, status: r.data.status } : x));
      if (r.data.status === "done") toast.success("Nice — one down 🎉");
      load();
    } catch { toast.error("Update failed"); }
  };
  const remove = async (t) => {
    if (!window.confirm("Delete this task?")) return;
    try { await api.delete(`/tasks/${t.task_id}`); load(); }
    catch { toast.error("Delete failed"); }
  };

  if (!user) return (
    <div className="min-h-screen grid place-items-center p-4">
      <div className="text-center"><h1 className="font-black text-xl">Sign in to use tasks</h1></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-stone-50" data-testid="tasks-page">
      <div className="max-w-3xl mx-auto p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-widest font-black text-stone-500">Astro-guided to-do</div>
            <h1 className="heading text-3xl font-black text-stone-900">Tasks</h1>
          </div>
          <div className="flex gap-1">
            {(typeof Notification !== "undefined" && Notification.permission !== "granted") && (
              <Button onClick={askNotifs} variant="outline" className="rounded-full font-black" data-testid="tasks-notif-enable">
                <Bell size={13} className="mr-1"/> Reminders
              </Button>
            )}
            <Button onClick={() => setShowAdd(true)} className="rounded-full bg-amber-500 hover:bg-amber-600 text-stone-900 font-black" data-testid="tasks-add-btn">
              <Plus size={13} className="mr-1"/> Add task
            </Button>
          </div>
        </div>

        {/* Astro widget — "best days this week" per user's tagged intents */}
        {widget && widget.intents?.length > 0 && (
          <div className="rounded-3xl bg-gradient-to-br from-purple-50 to-amber-50 border border-purple-200 p-3" data-testid="tasks-astro-widget">
            <div className="text-[11px] uppercase tracking-widest font-black text-purple-800 flex items-center gap-1">
              <Star size={11}/> Best days this week
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1 mt-2 -mx-1 px-1">
              {widget.intents.map(i => {
                const meta = INTENTS.find(x => x.k === i.intent) || { e: "🌟", best: i.weekdays.join(" · ") };
                return (
                  <div key={i.intent} className="min-w-[150px] rounded-2xl bg-white p-2.5 border border-purple-100 shrink-0" data-testid={`astro-widget-${i.intent}`}>
                    <div className="flex items-center justify-between">
                      <div className="text-xs font-black uppercase text-purple-900">{meta.e} {i.intent}</div>
                      {i.task_count > 0 && <span className="text-[9px] bg-purple-100 text-purple-800 rounded-full px-1.5 font-black">{i.task_count} task{i.task_count > 1 ? "s" : ""}</span>}
                    </div>
                    <div className="text-[10px] text-stone-500 mt-1">Auspicious: {i.weekdays.join(", ")}</div>
                    <div className="mt-1.5 space-y-0.5">
                      {(i.next_dates || []).slice(0, 3).map(d => (
                        <div key={d} className="text-[11px] font-mono font-black text-amber-700">✨ {d}</div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {summary && (
          <div className="grid grid-cols-4 gap-2">
            <StatBox label="Open"      value={summary.open}       t="stone"/>
            <StatBox label="Due today" value={summary.due_today}  t="amber"/>
            <StatBox label="Overdue"   value={summary.overdue}    t="rose"/>
            <StatBox label="Done today" value={summary.done_today} t="emerald"/>
          </div>
        )}

        <div className="flex gap-1 flex-wrap">
          {["open", "done", ""].map(s => (
            <button key={s} onClick={() => setFilter(f => ({ ...f, status: s }))}
              className={`text-xs font-black rounded-full px-3 py-1 ${filter.status === s ? "bg-stone-900 text-white" : "bg-white border border-stone-200 text-stone-700 hover:border-stone-400"}`}
              data-testid={`tasks-filter-${s || "all"}`}>
              {s === "" ? "All" : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
          <div className="ml-auto flex gap-1">
            {lists.map(l => (
              <button key={l.list_id} onClick={() => setFilter(f => ({ ...f, list_id: f.list_id === l.list_id ? null : l.list_id }))}
                className={`text-[11px] font-black rounded-full px-2.5 py-1 border ${filter.list_id === l.list_id ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-700 border-stone-200 hover:border-stone-400"}`}
                data-testid={`tasks-list-${l.list_id}`}>
                {l.emoji} {l.name}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center"><Loader2 className="inline animate-spin" size={20}/></div>
        ) : tasks.length === 0 ? (
          <div className="p-12 text-center text-sm text-stone-500">
            No tasks. Click "Add task" to start. Tag it with an intent (money, love…) to see auspicious days.
          </div>
        ) : (
          <ul className="space-y-2">
            {tasks.map(t => (
              <li key={t.task_id} className={`rounded-2xl border p-3 flex gap-3 ${t.status === "done" ? "opacity-60 bg-stone-100" : `bg-white ${PRIORITY_BG[t.priority] || "border-stone-200"}`}`}
                  data-testid={`task-${t.task_id}`}>
                <button onClick={() => toggle(t)} className="mt-0.5 shrink-0" data-testid={`task-toggle-${t.task_id}`}>
                  {t.status === "done"
                    ? <CheckSquare size={18} className="text-emerald-600"/>
                    : <Square size={18} className={PRIORITY_COLOR[t.priority] || "text-stone-500"}/>}
                </button>
                <div className="flex-1 min-w-0">
                  <div className={`font-black ${t.status === "done" ? "line-through text-stone-500" : "text-stone-900"}`}>
                    {t.title}
                  </div>
                  {t.notes && <p className="text-xs text-stone-600 mt-0.5">{t.notes}</p>}
                  <div className="flex flex-wrap gap-1.5 mt-1 text-[10px]">
                    {t.due_at && (
                      <span className="inline-flex items-center gap-1 bg-stone-100 rounded-full px-2 py-0.5">
                        <Calendar size={9}/> {t.due_at.slice(0, 10)}
                      </span>
                    )}
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 ${PRIORITY_BG[t.priority] || "bg-stone-100"}`}>
                      <Flag size={9}/> {t.priority}
                    </span>
                    {t.astro_intent && (
                      <span className="inline-flex items-center gap-1 bg-purple-50 text-purple-800 rounded-full px-2 py-0.5" data-testid={`task-intent-${t.task_id}`}>
                        <Sparkles size={9}/> {INTENTS.find(i => i.k === t.astro_intent)?.e} {t.astro_intent}
                      </span>
                    )}
                    {t.best_days?.length > 0 && t.status !== "done" && (
                      <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-800 rounded-full px-2 py-0.5" title="Auspicious days for this intent">
                        ✨ best: {t.best_days.slice(0, 2).map(d => d.slice(5)).join(", ")}
                      </span>
                    )}
                    {(t.tags || []).map((tag, i) => (
                      <span key={i} className="rounded-full bg-stone-100 text-stone-600 px-2 py-0.5">#{tag}</span>
                    ))}
                  </div>
                </div>
                <button onClick={() => remove(t)} className="text-stone-400 hover:text-red-600 shrink-0" data-testid={`task-del-${t.task_id}`}>
                  <Trash2 size={14}/>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {showAdd && <TaskCreateModal lists={lists} onClose={saved => { setShowAdd(false); if (saved) load(); }}/>}
    </div>
  );
}

function StatBox({ label, value, t }) {
  const cls = { stone: "bg-white text-stone-800", amber: "bg-amber-50 text-amber-800",
                rose: "bg-rose-50 text-rose-800", emerald: "bg-emerald-50 text-emerald-800" }[t] || "bg-white";
  return (
    <div className={`rounded-2xl border border-stone-200 p-3 text-center ${cls}`} data-testid={`tasks-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-lg font-black">{value}</div>
      <div className="text-[10px] uppercase tracking-widest font-black opacity-70">{label}</div>
    </div>
  );
}

function TaskCreateModal({ lists, onClose }) {
  const [f, setF] = useState({ title: "", notes: "", due_at: "", priority: "med", astro_intent: "", list_id: lists[0]?.list_id || "", tags: "" });
  const [busy, setBusy] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const save = async () => {
    if (!f.title.trim()) { toast.error("Title required"); return; }
    setBusy(true);
    try {
      await api.post("/tasks", {
        title: f.title.trim(),
        notes: f.notes.trim() || null,
        due_at: f.due_at || null,
        priority: f.priority,
        astro_intent: f.astro_intent || null,
        list_id: f.list_id || null,
        tags: f.tags.split(",").map(t => t.trim()).filter(Boolean),
      });
      toast.success("Task added");
      onClose(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 grid place-items-center p-4" style={{ zIndex: "var(--z-modal)" }} onClick={() => onClose(false)}>
      <div onClick={e => e.stopPropagation()} className="rounded-3xl bg-white p-5 w-full max-w-md space-y-3 max-h-[92vh] overflow-y-auto" data-testid="task-create-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-black text-stone-900">New task</h3>
          <button onClick={() => onClose(false)} className="text-stone-400 hover:text-stone-900"><X size={16}/></button>
        </div>
        <Input placeholder="What needs to be done?" value={f.title} onChange={e => setF({ ...f, title: e.target.value })} className="rounded-full text-sm" data-testid="task-input-title" autoFocus/>
        <div>
          <div className="text-[10px] uppercase tracking-widest font-black text-stone-500 mb-1">Astro intent (optional)</div>
          <div className="grid grid-cols-4 gap-1.5">
            {INTENTS.map(i => (
              <button key={i.k} onClick={() => setF({ ...f, astro_intent: f.astro_intent === i.k ? "" : i.k })}
                className={`rounded-2xl p-2 text-center text-[10px] font-black ${f.astro_intent === i.k ? "bg-purple-500 text-white" : "bg-purple-50 text-purple-800 hover:bg-purple-100"}`}
                data-testid={`task-intent-btn-${i.k}`}>
                <div className="text-lg">{i.e}</div>
                <div className="uppercase mt-0.5">{i.k}</div>
              </button>
            ))}
          </div>
          {f.astro_intent && (
            <div className="text-[10px] text-amber-700 mt-1">
              ✨ Best days: {INTENTS.find(i => i.k === f.astro_intent)?.best} — I'll pin the next matching date.
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-black text-stone-500 mb-1">Priority</div>
            <div className="flex gap-1">
              {["low", "med", "high"].map(p => (
                <button key={p} onClick={() => setF({ ...f, priority: p })}
                  className={`flex-1 rounded-full text-xs font-black py-1.5 border ${f.priority === p ? "bg-stone-900 text-white border-stone-900" : "border-stone-200 hover:border-stone-400"}`}
                  data-testid={`task-priority-${p}`}>
                  {p}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-black text-stone-500 mb-1">Due</div>
            <Input type="date" value={f.due_at} onChange={e => setF({ ...f, due_at: e.target.value })} className="rounded-full text-xs" data-testid="task-input-due"/>
          </div>
        </div>
        <button onClick={() => setShowMore(m => !m)} className="text-[11px] font-black text-stone-700 inline-flex items-center gap-1">
          {showMore ? <ChevronUp size={12}/> : <ChevronDown size={12}/>} More options
        </button>
        {showMore && (
          <>
            <Textarea rows={2} placeholder="Notes" value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} className="rounded-2xl text-sm" data-testid="task-input-notes"/>
            <Input placeholder="Tags (comma separated)" value={f.tags} onChange={e => setF({ ...f, tags: e.target.value })} className="rounded-full text-sm"/>
            {lists.length > 1 && (
              <select value={f.list_id} onChange={e => setF({ ...f, list_id: e.target.value })} className="w-full rounded-full text-sm border border-stone-200 px-3 py-2" data-testid="task-input-list">
                {lists.map(l => <option key={l.list_id} value={l.list_id}>{l.emoji} {l.name}</option>)}
              </select>
            )}
          </>
        )}
        <Button onClick={save} disabled={busy} className="w-full rounded-full bg-amber-500 hover:bg-amber-600 text-stone-900 font-black h-10" data-testid="task-save">
          {busy ? <Loader2 size={14} className="animate-spin mr-1"/> : <Plus size={14} className="mr-1"/>} Add task
        </Button>
      </div>
    </div>
  );
}
